import { useCallback, useEffect, useState } from "react";
import { Copy, Dices, Percent, Plus, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emptyForm = { code: "", kind: "percentage", percentage: 10, active: true };

function benefitLabel(item) {
  return item.kind === "two_for_one" ? "2x1" : `${item.percentage}%`;
}

export default function AdminDiscounts({ pin }) {
  const [discounts, setDiscounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { "x-admin-pin": pin };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/tickets/admin/discounts", { headers: { "x-admin-pin": pin } });
      setDiscounts(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudieron cargar los códigos.");
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { load(); }, [load]);

  const randomCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let suffix = "";
    for (let index = 0; index < 6; index += 1) suffix += chars[Math.floor(Math.random() * chars.length)];
    setForm((current) => ({ ...current, code: `CG-${suffix}` }));
  };

  const create = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets/admin/discounts", {
        ...form,
        code: form.code.toUpperCase(),
        percentage: form.kind === "percentage" ? Number(form.percentage) : 0,
      }, { headers });
      toast.success("Código de descuento creado.");
      setForm(emptyForm);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo crear el código.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/tickets/admin/discounts/${encodeURIComponent(item.code)}`, {
        code: item.code,
        kind: item.kind,
        percentage: item.percentage,
        active: !item.active,
      }, { headers });
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar el código.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar el código ${item.code}?`)) return;
    try {
      await api.delete(`/tickets/admin/discounts/${encodeURIComponent(item.code)}`, { headers });
      toast.success("Código eliminado.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo eliminar el código.");
    }
  };

  const copyCode = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success("Código copiado.");
  };

  return (
    <div className="space-y-6">
      <section className="card-cg bg-white p-5 md:p-6">
        <div className="flex items-start gap-3">
          <Tag className="text-[var(--brand-primary)]" size={23} />
          <div><h2 className="font-serif-cg text-2xl">Códigos de descuento</h2><p className="text-sm text-[var(--brand-muted)] mt-1">El descuento sólo reduce el precio de los boletos. El cargo de servicio de $40.00 por boleto no cambia.</p></div>
        </div>

        <form onSubmit={create} className="grid md:grid-cols-2 lg:grid-cols-[1.2fr_1fr_0.7fr_auto] gap-4 mt-6 items-end">
          <label className="block">
            <span className="overline text-[var(--brand-muted)]">Código</span>
            <div className="mt-2 flex">
              <input required minLength={3} maxLength={24} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") })} placeholder="VERANO20" className="w-full border border-[var(--brand-border)] px-3 py-2.5 uppercase outline-none focus:border-[var(--brand-primary)]" />
              <button type="button" onClick={randomCode} className="h-11 w-11 shrink-0 border border-l-0 border-[var(--brand-border)] inline-flex items-center justify-center" title="Generar código aleatorio" aria-label="Generar código aleatorio"><Dices size={17} /></button>
            </div>
          </label>
          <label className="block">
            <span className="overline text-[var(--brand-muted)]">Promoción</span>
            <select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })} className="mt-2 h-11 w-full border border-[var(--brand-border)] bg-white px-3 text-sm">
              <option value="percentage">Porcentaje</option>
              <option value="two_for_one">2x1</option>
            </select>
          </label>
          {form.kind === "percentage" ? (
            <label className="block">
              <span className="overline text-[var(--brand-muted)]">Descuento</span>
              <div className="relative mt-2"><input required type="number" min="10" max="50" value={form.percentage} onChange={(event) => setForm({ ...form, percentage: Number(event.target.value) })} className="h-11 w-full border border-[var(--brand-border)] px-3 pr-9 outline-none focus:border-[var(--brand-primary)]" /><Percent size={15} className="absolute right-3 top-3.5 text-[var(--brand-muted)]" /></div>
            </label>
          ) : <div className="text-sm text-[var(--brand-muted)] pb-3">Mínimo 2 boletos</div>}
          <button disabled={saving} className="btn-primary-cg h-11 justify-center disabled:opacity-50"><Plus size={16} /> {saving ? "Creando..." : "Crear código"}</button>
        </form>
      </section>

      <section className="card-cg bg-white p-5 md:p-6 overflow-hidden">
        <div className="flex items-baseline justify-between gap-4"><h2 className="font-serif-cg text-2xl">Códigos creados</h2><span className="text-sm text-[var(--brand-muted)]">{discounts.length} en total</span></div>
        {loading ? <p className="mt-5 text-sm text-[var(--brand-muted)]">Cargando...</p> : discounts.length ? (
          <div className="overflow-x-auto mt-5">
            <table className="w-full min-w-[650px] text-sm">
              <thead><tr className="border-b text-left overline text-[var(--brand-muted)]"><th className="py-3">Código</th><th>Beneficio</th><th>Usos</th><th>Estado</th><th className="text-right">Acciones</th></tr></thead>
              <tbody>{discounts.map((item) => (
                <tr key={item.code} className="border-b border-[var(--brand-border)]">
                  <td className="py-4 font-mono font-semibold">{item.code}</td>
                  <td>{benefitLabel(item)}</td>
                  <td>{item.uses || 0}</td>
                  <td><button type="button" onClick={() => toggle(item)} className={`px-3 py-1.5 text-xs font-semibold border ${item.active ? "border-green-300 bg-green-50 text-green-800" : "border-gray-300 text-gray-600"}`}>{item.active ? "Activo" : "Inactivo"}</button></td>
                  <td><div className="flex justify-end gap-2"><button type="button" onClick={() => copyCode(item.code)} className="h-9 w-9 inline-flex items-center justify-center border border-[var(--brand-border)]" title="Copiar" aria-label={`Copiar ${item.code}`}><Copy size={15} /></button><button type="button" onClick={() => remove(item)} className="h-9 w-9 inline-flex items-center justify-center border border-red-200 text-red-700" title="Eliminar" aria-label={`Eliminar ${item.code}`}><Trash2 size={15} /></button></div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="mt-5 text-sm text-[var(--brand-muted)]">Todavía no hay códigos. Crea el primero arriba.</p>}
      </section>
    </div>
  );
}
